import { inject, Injectable } from '@angular/core';
import { PortalFieldConfig } from '@portal/form/data-access';
import {
  CollateralPropertySubTypeOfCommercials,
  CollateralPropertySubTypeOfIndustrials,
  CollateralPropertySubTypeOfResidentials,
  DEFAULT_CONFIG,
} from '@portal/data-access';
import { CollateralPropertyModelKeys } from '../const/form-field-key.const';
import { AddressFormConfigService } from './address-form-config.service';
import {
  CollateralPropertyCompletionStatus,
  CollateralPropertyTenure,
  CollateralPropertyType,
  COMMON_VALIDATION,
  SQRT_SQM,
} from '@core/data-access';

@Injectable({ providedIn: 'root' })
export class CollateralPropertyFormConfigService {
  private addressFormConfigService = inject(AddressFormConfigService);

  getCollateralPropertyFormFields(): PortalFieldConfig[] {
    return [
      {
        template: '<div class="portal-paragraph">Help us update information regarding your property</div>',
      },
      {
        fieldGroupClassName: 'field-columns',
        fieldGroup: [
          {
            key: CollateralPropertyModelKeys.purchasePrice,
            type: 'input',
            props: {
              label: 'Property Purchase Price',
              placeholder: 'Please enter input',
              required: true,
              prefix: 'SGD',
              maskConfig: { mask: 'separator.2' },
              max: DEFAULT_CONFIG.MAX_VALUE.DECIMAL,
            },
          },
          {
            key: CollateralPropertyModelKeys.purchaseDateOnly,
            type: 'datepicker',
            defaultValue: null,
            props: {
              label: 'Property Purchased Date',
              placeholder: 'Please enter input',
            },
          },
          {
            key: CollateralPropertyModelKeys.address,
            className: 'sm:col-span-2',
            fieldGroupClassName: 'field-columns',
            fieldGroup: this.addressFormConfigService.initLocalAddressFormFields(false),
          },
          {
            key: CollateralPropertyModelKeys.propertyLandTitleDetails,
            type: '#propertyLandTitle',
            props: { label: 'Share with us the details of the property' },
          },
          {
            key: CollateralPropertyModelKeys.propertyType,
            type: '#propertyType',
            props: { required: true },
          },
          {
            key: CollateralPropertyModelKeys.propertySubType,
            type: 'mat-select',
            props: {
              label: 'Property Sub-Type',
              required: true,
              options: CollateralPropertySubTypeOfIndustrials,
            },
            expressions: {
              'props.options': ({ model }) => {
                switch (model?.[CollateralPropertyModelKeys.propertyType]) {
                  case CollateralPropertyType.COMMERCIAL:
                    return CollateralPropertySubTypeOfCommercials;
                  case CollateralPropertyType.INDUSTRIAL:
                    return CollateralPropertySubTypeOfIndustrials;
                  case CollateralPropertyType.RESIDENTIAL:
                    return CollateralPropertySubTypeOfResidentials;
                  default:
                    return [];
                }
              },
            },
          },
        ],
      },
      {
        template: '<div class="portal-paragraph">Share with us the size of the property.</div>',
      },
      {
        fieldGroupClassName: 'field-columns',
        fieldGroup: [
          {
            wrappers: ['form-field'],
            props: { label: 'Built-in Area', wrapperClass: '!mb-0' },
            fieldGroupClassName: 'flex gap-4',
            fieldGroup: [
              {
                key: CollateralPropertyModelKeys.buildInArea,
                className: 'flex-1',
                type: 'input',
                props: {
                  placeholder: 'e.g. 5721',
                  maskConfig: { mask: 'separator.2' },
                  max: SQRT_SQM.MAX,
                },
              },
              {
                key: CollateralPropertyModelKeys.buildInAreaUnit,
                className: 'max-w-32',
                type: '#areaUnit',
                props: { placeholder: 'Built-in Area Unit' },
              },
            ],
          },
          {
            wrappers: ['form-field'],
            props: { label: 'Land Area', wrapperClass: '!mb-0' },
            fieldGroupClassName: 'flex gap-4',
            fieldGroup: [
              {
                key: CollateralPropertyModelKeys.landArea,
                className: 'flex-1',
                type: 'input',
                props: {
                  placeholder: 'e.g. 5721',
                  maskConfig: { mask: 'separator.2' },
                  max: SQRT_SQM.MAX,
                },
              },
              {
                key: CollateralPropertyModelKeys.landAreaUnit,
                className: 'max-w-32',
                type: '#areaUnit',
                props: { placeholder: 'Land Area Unit' },
              },
            ],
          },
          {
            key: CollateralPropertyModelKeys.completionStatus,
            type: '#propertyCompletionStatus',
            props: { label: 'Share with us the Completion Status' },
          },
          {
            key: CollateralPropertyModelKeys.expectedTOPOnly,
            type: 'datepicker',
            expressions: {
              hide: field =>
                field.model?.[CollateralPropertyModelKeys.completionStatus] !==
                CollateralPropertyCompletionStatus.BUILDING_UNDER_CONSTRUCTION,
            },
            props: {
              label: 'TOP date of the property',
              placeholder: 'eg. 18-03-2020',
            },
          },
          {
            key: CollateralPropertyModelKeys.completionDateOnly,
            type: 'datepicker',
            expressions: {
              hide: field =>
                field.model?.[CollateralPropertyModelKeys.completionStatus] !==
                CollateralPropertyCompletionStatus.BUILDING_UNDER_CONSTRUCTION,
            },
            props: {
              label: 'Certificate of Statutory Completion Date',
              placeholder: 'eg. 18-03-2020',
            },
          },
          {
            key: CollateralPropertyModelKeys.lastRenovationDateOnly,
            type: 'datepicker',
            expressions: {
              hide: field =>
                ![
                  CollateralPropertyCompletionStatus.COMPLETED,
                  CollateralPropertyCompletionStatus.COMPLETED_PENDING_CSC,
                  CollateralPropertyCompletionStatus.COMPLETED_PROGRESSIVE_DISBURSEMENT,
                ].includes(field.model?.[CollateralPropertyModelKeys.completionStatus]),
            },
            props: {
              label: 'Last Renovation Date',
              placeholder: 'eg. 18-03-2020',
            },
          },
          {
            key: CollateralPropertyModelKeys.renovationCost,
            type: 'input',
            expressions: {
              hide: field =>
                ![
                  CollateralPropertyCompletionStatus.COMPLETED,
                  CollateralPropertyCompletionStatus.COMPLETED_PENDING_CSC,
                  CollateralPropertyCompletionStatus.COMPLETED_PROGRESSIVE_DISBURSEMENT,
                ].includes(field.model?.[CollateralPropertyModelKeys.completionStatus]),
            },
            props: {
              label: 'Renovation Cost',
              placeholder: 'e.g. 200,000',
              prefix: 'SGD',
              maskConfig: { mask: 'separator.2' },
              max: DEFAULT_CONFIG.MAX_VALUE.DECIMAL,
            },
          },
          {
            key: CollateralPropertyModelKeys.tenure,
            type: '#propertyTenure',
          },
          {
            key: CollateralPropertyModelKeys.leaseWEFOnly,
            type: 'datepicker',
            props: {
              label: 'Leasehold Effective Date',
              placeholder: 'eg. 18-03-2020',
            },
            expressions: {
              'props.required': ({ model }) => model?.[CollateralPropertyModelKeys.tenure] === CollateralPropertyTenure.LEASEHOLD,
            },
          },
          {
            key: CollateralPropertyModelKeys.nameOfExistingRegisteredOwner,
            type: 'input',
            props: {
              label: 'Existing Registered Owner (For Completed Property)',
              placeholder: 'e.g. Adam Zachary',
            },
          },
          {
            key: CollateralPropertyModelKeys.nameOfProposedRegisteredOwner,
            type: 'input',
            props: {
              label: 'Proposed Registered Owner',
              placeholder: 'e.g. Lim Meng Teck',
              maxLength: COMMON_VALIDATION.MAX_LENGTH,
            },
          },
          {
            key: CollateralPropertyModelKeys.purpose,
            type: '#propertyPurpose',
            props: { label: 'Purpose' },
          },
          {
            key: CollateralPropertyModelKeys.cpfUtilisedAmount,
            type: 'input',
            props: {
              label: 'Utilised CPF Amount',
              placeholder: 'e.g. 200,000',
              prefix: 'SGD',
              maskConfig: { mask: 'separator.2' },
              max: DEFAULT_CONFIG.MAX_VALUE.DECIMAL,
            },
          },
          {
            key: CollateralPropertyModelKeys.outstandingAmount,
            type: 'input',
            props: {
              label: 'Outstanding Amount',
              placeholder: 'e.g. 200,000',
              prefix: 'SGD',
              maskConfig: { mask: 'separator.2' },
              max: DEFAULT_CONFIG.MAX_VALUE.DECIMAL,
            },
          },
          {
            key: CollateralPropertyModelKeys.usage,
            type: '#propertyUsage',
          },
          {
            key: CollateralPropertyModelKeys.income,
            type: 'input',
            props: {
              label: 'Rental Saving/Income',
              placeholder: 'e.g. 200,000',
              prefix: 'SGD',
              maskConfig: { mask: 'separator.2' },
              max: DEFAULT_CONFIG.MAX_VALUE.DECIMAL,
            },
          },
        ],
      },
    ];
  }
}
